import { useState, type FormEvent } from "react";

type Plan = Record<string, any>;

export const suggestedMonthlyPrices: Record<string, string> = {
  "cr-home-phone": "19.95",
  "cr-business-phone": "24.95",
};

export function planPriceUpdate(plan: Plan, price: string, active: boolean) {
  return {
    name: plan.name,
    description: plan.description,
    kind: plan.kind,
    unit: plan.unit,
    features: plan.features ?? [],
    version: Number(plan.version),
    monthlyPrice: price.trim(),
    active,
  };
}

export function PlanPriceEditor({
  plan,
  busy,
  onSave,
}: {
  plan: Plan;
  busy: boolean;
  onSave: (body: ReturnType<typeof planPriceUpdate>) => Promise<void>;
}) {
  // Hold the revision displayed when editing began; a refresh must not overwrite another operator's change.
  const [original] = useState(plan);
  const initialPrice = original.price_configured
    ? (Number(original.recurring_minor) / 100).toFixed(2)
    : "";
  const [price, setPrice] = useState(initialPrice);
  const [active, setActive] = useState(Boolean(original.active));
  const suggestion = suggestedMonthlyPrices[original.code];
  const changed =
    price.trim() !== initialPrice || active !== Boolean(original.active);
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    await onSave(planPriceUpdate(original, price, active));
  }
  return (
    <form
      className="cr-form"
      aria-label={`${original.name} pricing`}
      onSubmit={submit}
    >
      <fieldset className="cr-form-fields" disabled={busy}>
        <div className="cr-form-grid">
          <label className="cr-field">
            <span>
              {original.unit === "seat"
                ? "Monthly price per user (USD)"
                : "Monthly plan price (USD)"}
            </span>
            <input
              name="monthlyPrice"
              inputMode="decimal"
              pattern="[0-9]{1,6}(\.[0-9]{1,2})?"
              required={active}
              value={price}
              onChange={(event) => setPrice(event.target.value)}
              aria-describedby={`quick-price-help-${original.id}`}
            />
            <small id={`quick-price-help-${original.id}`}>
              Base service price, before taxes and separately quoted equipment.
            </small>
          </label>
          <label className="cr-field">
            <span>Availability for new selections</span>
            <select
              value={active ? "available" : "unavailable"}
              onChange={(event) =>
                setActive(event.target.value === "available")
              }
            >
              <option value="unavailable">Not offered</option>
              <option value="available">Available</option>
            </select>
            <small>
              Existing customer service and subscriptions stay unchanged.
            </small>
          </label>
        </div>
        {suggestion && (
          <p>
            Suggested starting rate: ${suggestion}
            {original.unit === "seat" ? " per user/month" : "/month"}.{" "}
            <button
              className="cr-button secondary"
              type="button"
              onClick={() => setPrice(suggestion)}
              disabled={price === suggestion}
            >
              Use ${suggestion}
            </button>
          </p>
        )}
        {original.code === "cr-business-trunk" && (
          <p>
            Quote trunk service after agreeing on concurrent calls, included
            minutes and usage charges. There is no flat-rate recommendation for
            this plan.
          </p>
        )}
        <button className="cr-button" type="submit" disabled={!changed}>
          {busy ? "Saving price…" : "Save price & availability"}
        </button>
      </fieldset>
    </form>
  );
}
